import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Eye,
  Package,
  DollarSign,
  Bell,
  BellOff,
  Receipt,
  Printer,
  Download,
  Share2,
  Smartphone,
  Car,
} from 'lucide-react';
import OrderModal from '../components/OrderModal';
import { printKotViaBluetooth } from '../utils/kotBluetoothPrint';

const Orders = () => {
  // Add print styles for KOT
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #kot-content, #kot-content * {
          visibility: visible;
        }
        #kot-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .no-print {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showKotView, setShowKotView] = useState(false);
  const [kotOrder, setKotOrder] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(() => {
    const saved = localStorage.getItem('notificationSettings');
    return saved
      ? JSON.parse(saved)
      : {
          sound: true,
          browser: true,
          toast: true,
        };
  });
  const [loadingStates, setLoadingStates] = useState({});
  const [bluetoothPrintingOrderId, setBluetoothPrintingOrderId] =
    useState(null);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { vendor } = useAuth();

  const {
    data: orders,
    isLoading,
    error,
  } = useQuery(
    [
      'orders',
      statusFilter,
      orderTypeFilter,
      paymentMethodFilter,
      dateRangeFilter,
    ],
    async () => {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (orderTypeFilter) params.order_type = orderTypeFilter;
      if (paymentMethodFilter) params.payment_method = paymentMethodFilter;
      if (dateRangeFilter) params.date_range = dateRangeFilter;

      const response = await api.get('/vendor/orders', { params });
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error('Orders query error:', error);
      },
    },
  );

  const updateOrderStatusMutation = useMutation(
    async ({ orderId, status }) => {
      const response = await api.post(`/vendor/orders/${orderId}/status`, {
        status,
      });
      return response.data;
    },
    {
      onMutate: ({ orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: (data, { orderId }) => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast('Order status updated successfully!', 'order-status');
        if (data?.order) {
          setSelectedOrder((prev) => {
            if (!prev || prev.id !== data.order.id) return prev;
            return {
              ...prev,
              status: data.order.status,
              updated_at: data.order.updated_at,
            };
          });
        }
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || 'Failed to update order status';
        showErrorToast(errorMessage, 'order-status');
        console.error(
          'Order status update error:',
          error.response?.data || error.message,
        );
      },
      onSettled: (_, __, { orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    },
  );

  const approveCashOrderMutation = useMutation(
    async (orderId) => {
      const response = await api.put(`/vendor/orders/${orderId}/approve-cash`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast('Cash order approved successfully!', 'cash-approval');
        setSelectedOrder(null);
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to approve cash order',
          'cash-approval',
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    },
  );

  // New mutation for generating KOT
  const generateKotMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/vendor/orders/${orderId}/generate-kot`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: true }));
      },
      onSuccess: (data, orderId) => {
        queryClient.invalidateQueries(['orders']);
        showSuccessToast(
          `KOT generated successfully! Reference: ${data.kot_reference}`,
          'kot-generation',
        );
        setSelectedOrder(null);

        // Find the order and open KOT modal
        if (orders) {
          const order = orders.find(
            (o) => o.id.toString() === orderId.toString(),
          );
          if (order) {
            const updatedOrder = {
              ...order,
              kot_reference: data.kot_reference,
            };
            setKotOrder(updatedOrder);
            setShowKotView(true);
          }
        }
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to generate KOT',
          'kot-generation',
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: false }));
      },
    },
  );

  const filteredOrders =
    orders
      ?.filter(
        (order) =>
          order.id.toString().includes(searchTerm) ||
          order.user_id.toString().includes(searchTerm) ||
          order.order_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.payment_method
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (order.orderItems &&
            order.orderItems.some((item) =>
              item.menuItem?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()),
            )),
      )
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case 'date':
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
          case 'total':
            aValue = parseFloat(a.total_amount);
            bValue = parseFloat(b.total_amount);
            break;
          case 'id':
            aValue = parseInt(a.id);
            bValue = parseInt(b.id);
            break;
          default:
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
        }

        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }) || [];

  // Listen for real-time order updates
  useEffect(() => {
    if (!socket) return;

    const handleOrderNotification = (notification) => {
      console.log(
        '📦 Received order notification in Orders page:',
        notification,
      );

      // Refresh the orders data when we receive notifications
      queryClient.invalidateQueries(['orders']);
    };

    // Subscribe to order notifications
    socket.on('order-notification', handleOrderNotification);

    return () => {
      socket.off('order-notification', handleOrderNotification);
    };
  }, [socket, queryClient]);

  // Handle URL parameters for direct order access
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderId = searchParams.get('order');
    const action = searchParams.get('action');

    if (orderId && orders) {
      const order = orders.find((o) => o.id.toString() === orderId);
      if (order) {
        setSelectedOrder(order);

        // Show action-specific toast message
        if (action === 'view') {
          showSuccessToast(`Order #${orderId} details opened`, 'order-view');
        } else if (action === 'status') {
          showSuccessToast(
            `Order #${orderId} status updated successfully`,
            'order-status',
          );
        }

        // Clear URL parameters after handling
        navigate('/orders', { replace: true });
      } else {
        showErrorToast(`Order #${orderId} not found`, 'order-not-found');
        navigate('/orders', { replace: true });
      }
    }
  }, [orders, location.search, navigate]);

  // Enhanced status update with better feedback
  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatusMutation.mutateAsync({ orderId, status });

      // Show enhanced success message
      showSuccessToast(
        `Order #${orderId} status changed to ${status}`,
        'order-status-update',
      );
    } catch (error) {
      // Enhanced error message
      showErrorToast(
        error.response?.data?.message || 'Failed to update order status',
        'order-status-update',
      );
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setOrderTypeFilter('');
    setPaymentMethodFilter('');
    setDateRangeFilter('');
    setSortBy('date');
    setSortOrder('desc');
  };

  const updateNotificationSettings = (newSettings) => {
    const updatedSettings = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updatedSettings);
    localStorage.setItem(
      'notificationSettings',
      JSON.stringify(updatedSettings),
    );
  };

  const hasActiveFilters =
    searchTerm ||
    statusFilter ||
    orderTypeFilter ||
    paymentMethodFilter ||
    dateRangeFilter;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval':
        return 'bg-orange-100 text-orange-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'preparing':
        return 'bg-orange-100 text-orange-800';
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getNextStatus = (currentStatus, paymentMethod) => {
    switch (currentStatus) {
      case 'pending':
        return 'confirmed';
      case 'pending_approval':
        return paymentMethod === 'cash_at_counter' ? 'confirmed' : 'confirmed';
      case 'confirmed':
        return 'preparing';
      case 'preparing':
        return 'ready';
      case 'ready':
        return 'completed';
      case 'completed':
        return null; // No further transitions
      case 'cancelled':
        return null; // No further transitions
      default:
        return null;
    }
  };

  const getStatusFlow = (order) => {
    const statusFlow = [
      {
        status: 'pending',
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800',
        icon: '⏳',
      },
      {
        status: 'pending_approval',
        label: 'Pending Approval',
        color: 'bg-orange-100 text-orange-800',
        icon: '🔍',
      },
      {
        status: 'confirmed',
        label: 'Confirmed',
        color: 'bg-blue-100 text-blue-800',
        icon: '✅',
      },
      {
        status: 'preparing',
        label: 'Preparing',
        color: 'bg-purple-100 text-purple-800',
        icon: '👨‍🍳',
      },
      {
        status: 'ready',
        label: 'Ready',
        color: 'bg-green-100 text-green-800',
        icon: '🎉',
      },
      {
        status: 'completed',
        label: 'Completed',
        color: 'bg-green-100 text-green-800',
        icon: '✅',
      },
      {
        status: 'cancelled',
        label: 'Cancelled',
        color: 'bg-red-100 text-red-800',
        icon: '❌',
      },
    ];

    const currentIndex = statusFlow.findIndex((s) => s.status === order.status);
    const nextStatus = getNextStatus(order.status, order.payment_method);
    const nextIndex = statusFlow.findIndex((s) => s.status === nextStatus);

    return {
      flow: statusFlow,
      currentIndex,
      nextIndex,
      nextStatus,
      canProgress: nextIndex > currentIndex,
      canCancel:
        order.status === 'pending' || order.status === 'pending_approval',
    };
  };

  const getStatusAction = (order) => {
    const statusFlow = getStatusFlow(order);

    if (!statusFlow.nextStatus) return null;

    if (
      order.status === 'pending_approval' &&
      order.payment_method === 'cash_at_counter'
    ) {
      return {
        label: 'Approve Cash Order',
        action: () => approveCashOrderMutation.mutate(order.id),
        loading: approveCashOrderMutation.isLoading,
        color: 'bg-green-600 hover:bg-green-700',
        icon: '💵',
      };
    }

    return {
      label: `Mark as ${
        statusFlow.nextStatus.charAt(0).toUpperCase() +
        statusFlow.nextStatus.slice(1)
      }`,
      action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
      loading: updateOrderStatusMutation.isLoading,
      color: 'bg-primary-600 hover:bg-primary-700',
      icon: '➡️',
    };
  };

  // New function for inline status changes
  const getInlineStatusActions = (order) => {
    const statusFlow = getStatusFlow(order);
    const actions = [];
    const isOrderLoading = loadingStates[order.id] || false;

    // Add next status action
    if (statusFlow.nextStatus) {
      if (
        order.status === 'pending_approval' &&
        order.payment_method === 'cash_at_counter'
      ) {
        actions.push({
          label: 'Approve',
          action: () => approveCashOrderMutation.mutate(order.id),
          loading: isOrderLoading,
          color: 'bg-green-600 hover:bg-green-700',
          icon: '💵',
          size: 'sm',
        });
      } else {
        actions.push({
          label:
            statusFlow.nextStatus.charAt(0).toUpperCase() +
            statusFlow.nextStatus.slice(1),
          action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
          loading: isOrderLoading,
          color: 'bg-primary-600 hover:bg-primary-700',
          icon: '➡️',
          size: 'sm',
        });
      }
    }

    // Add cancel action for early stages
    if (statusFlow.canCancel) {
      actions.push({
        label: 'Cancel',
        action: () => {
          if (
            window.confirm(
              `Are you sure you want to cancel order #${order.id}?`,
            )
          ) {
            updateOrderStatusMutation.mutate({
              orderId: order.id,
              status: 'cancelled',
            });
          }
        },
        loading: isOrderLoading,
        color: 'bg-red-600 hover:bg-red-700',
        icon: '❌',
        size: 'sm',
      });
    }

    return actions;
  };

  // 58mm KOT print – same structure as reference (LUNAR PARK, Vendor, Order ID, items, Powered by KnockIt Technology)
  const KOT_WIDTH = 36;
  const SEP = '------------------------------------';

  const centerLine = (text) => {
    const len = Math.min(text.length, KOT_WIDTH);
    const pad = Math.max(0, Math.floor((KOT_WIDTH - len) / 2));
    return ' '.repeat(pad) + text.substring(0, KOT_WIDTH);
  };

  const LABEL_WIDTH = 12;
  const labelPartLength = LABEL_WIDTH + 2; // padded label + ': '
  const valueWidth = KOT_WIDTH - labelPartLength;

  const labelValue = (label, value) => {
    const labelPadded = (label + ' '.repeat(LABEL_WIDTH)).substring(
      0,
      LABEL_WIDTH,
    );
    const labelPart = labelPadded + ': ';
    const valuePart = String(value ?? '').substring(0, valueWidth);
    return labelPart + valuePart;
  };

  /** Returns multiple lines when value exceeds KOT width (for Add-ons etc.). Breaks at word boundaries when possible. */
  const wrapLabelValue = (label, value) => {
    const labelPadded = (label + ' '.repeat(LABEL_WIDTH)).substring(
      0,
      LABEL_WIDTH,
    );
    const labelPart = labelPadded + ': ';
    const text = String(value ?? '');
    if (text.length <= valueWidth) {
      return [labelPart + text];
    }
    const result = [];
    let remaining = text;
    let first = true;
    while (remaining.length > 0) {
      let end = Math.min(valueWidth, remaining.length);
      let chunk = remaining.substring(0, end);
      // Prefer break at last comma or space so we don't split words
      if (end < remaining.length && chunk.length === valueWidth) {
        const lastComma = chunk.lastIndexOf(', ');
        const lastSpace = chunk.lastIndexOf(' ');
        const breakAt =
          lastComma >= 0
            ? lastComma + 2
            : lastSpace >= 0
              ? lastSpace + 1
              : valueWidth;
        if (breakAt > valueWidth * 0.4) {
          chunk = remaining.substring(0, breakAt).trimEnd();
          end = breakAt;
        }
      }
      remaining = remaining.substring(end).trimStart();
      const prefix = first ? labelPart : ' '.repeat(labelPartLength);
      result.push(prefix + chunk);
      first = false;
    }
    return result;
  };

  const formatOrderDate = (dateStr) => {
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
    const timePart = d
      .toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
    return `${datePart} ${timePart}`;
  };

  /** Build the same KOT plain text for both browser print and Bluetooth print. */
  const getKotText = (order) => {
    const vendorName = vendor?.name || 'Vendor';
    const kotRef =
      order.kot_reference || order.kotReference || order.kot?.reference || '—';
    const orderId = `Order #${order.id}`;
    const orderType = (order.order_type || '').replace(/_/g, ' ').toUpperCase();
    const orderDate = formatOrderDate(order.created_at);

    const lines = [
      SEP,
      'LUNAR PARK',
      SEP,
      labelValue('KOT Ref', kotRef),
      labelValue('Order ID', orderId),
      labelValue('Vendor', vendorName),
      labelValue('Order Type', orderType),
      labelValue('Order Date', orderDate),
      SEP,
    ];

    (order.orderItems || []).forEach((item, index) => {
      const itemNum = index + 1;
      const category = item.categoryName || 'Uncategorized';
      const name =
        item.menuItem?.name || item.name || `Item #${item.menu_item_id}`;
      const addonNames = (item.addons || [])
        .map((a) => {
          const addonName =
            a.menuItemAddon?.name || a.name || `Addon #${a.menu_item_addon_id}`;
          const qty = a.quantity != null ? ` (x${a.quantity})` : '';
          return `${addonName}${qty}`;
        })
        .join(', ');
      lines.push(`Item ${itemNum}`);
      lines.push(labelValue('Category', category));
      lines.push(labelValue('Name', name));
      lines.push(labelValue('Qty', item.quantity));
      wrapLabelValue('Add-ons', addonNames || '—').forEach((line) =>
        lines.push(line),
      );
      lines.push(SEP);
    });
    const allergyNote = order.allergy_note || order.allergyNote || '—';
    wrapLabelValue('Allergy Note', allergyNote).forEach((line) =>
      lines.push(line),
    );
    lines.push(SEP);
    lines.push(labelValue('Total Items', order.orderItems?.length || 0));
    lines.push(SEP);
    lines.push('Powered by KnockIt Technology');

    return lines.join('\n');
  };

  const handleOrderPrint = (order) => {
    const kotText = getKotText(order);
    const orderId = `Order #${order.id}`;
    const kotReference = order.kot_reference;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>KOT ${kotReference || orderId}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 16px;
            background: #fff;
            font-family: "Courier New", Courier, "Lucida Console", monospace;
            font-size: 15px;
            line-height: 1.4;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          }
          #kot {
            width: 58mm;
            max-width: 58mm;
            word-break: break-all;
            text-align: left;
          }
          #kot .kot-line { display: block; width: 100%; white-space: pre; }
          #kot .kot-center { text-align: center; }
          @media print {
            @page { size: auto; margin: 10mm; }
            html, body {
              margin: 0;
              padding: 0;
              min-height: auto;
              height: auto;
              display: block;
              font-family: "Courier New", Courier, "Lucida Console", monospace;
              font-size: 15px;
              line-height: 1.4;
            }
            body { text-align: center; }
            #kot {
              width: 58mm;
              margin: 0 auto;
              display: inline-block;
              text-align: left;
            }
          }
        </style>
      </head>
      <body>
        <div id="kot">${kotText
          .split('\n')
          .map((line) => {
            const escaped = line.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const trim = line.trim();
            const isCenter =
              trim === 'LUNAR PARK' || trim === 'Powered by KnockIt Technology';
            return `<div class="kot-line ${isCenter ? 'kot-center' : ''}">${escaped}</div>`;
          })
          .join('')}</div>
      </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 500);
    };
  };

  const handleOrderPrintBluetooth = async (order) => {
    if (bluetoothPrintingOrderId !== null) return;
    setBluetoothPrintingOrderId(order.id);
    try {
      const kotText = getKotText(order);
      await printKotViaBluetooth(kotText);
      showSuccessToast('KOT sent to Bluetooth printer', 'kot-bluetooth');
    } catch (err) {
      const message =
        err?.message ||
        (err?.name === 'NotFoundError'
          ? 'No printer selected'
          : 'Bluetooth print failed');
      showErrorToast(message, 'kot-bluetooth');
    } finally {
      setBluetoothPrintingOrderId(null);
    }
  };

  // New function for KOT actions
  const getKotActions = (order) => {
    const actions = [];
    const isKotLoading = loadingStates[`kot-${order.id}`] || false;

    // Check for KOT reference in multiple possible locations
    const kotReference =
      order.kot_reference || order.kotReference || order.kot?.reference;

    if (kotReference) {
      // View KOT action
      actions.push({
        label: 'View KOT',
        action: () => {
          console.log(
            'Opening KOT for order:',
            order.id,
            'KOT ref:',
            kotReference,
          );
          setKotOrder(order);
          setShowKotView(true);
        },
        color: 'bg-green-600 hover:bg-green-700',
        icon: '📋',
        size: 'sm',
      });
    } else {
      // Generate KOT action - now opens KOT modal after generation
      actions.push({
        label: 'Generate KOT',
        action: () => {
          if (window.confirm(`Generate KOT for order #${order.id}?`)) {
            generateKotMutation.mutate(order.id);
          }
        },
        loading: isKotLoading,
        color: 'bg-blue-600 hover:bg-blue-700',
        icon: '🖨️',
        size: 'sm',
      });
    }

    return actions;
  };

  const KotModal = ({ order, onClose, on58mmPrint }) => {
    const handlePrint = () => {
      if (on58mmPrint) {
        on58mmPrint(order);
      } else {
        window.print();
      }
    };

    const handleDownload = () => {
      // Create a downloadable text version
      const kotContent = `
KITCHEN ORDER TICKET
====================

KOT Reference: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }
Order ID: #${order.id}
Date: ${new Date(order.created_at).toLocaleString()}

CUSTOMER DETAILS
----------------
Customer ID: ${order.user_id}
Order Type: ${order.order_type.toUpperCase()}

ORDER ITEMS
-----------
${
  order.orderItems
    ?.map((item) => {
      let itemText = `${item.quantity}x ${
        item.menuItem?.name || `Item #${item.menu_item_id}`
      } - £${item.price}`;
      if (item.addons && item.addons.length > 0) {
        itemText += '\n  Add-ons:';
        item.addons.forEach((addon) => {
          itemText += `\n    ${addon.quantity}x ${
            addon.menuItemAddon?.name || `Addon #${addon.menu_item_addon_id}`
          } - £${addon.price_at_time}`;
        });
      }
      return itemText;
    })
    .join('\n') || 'No items'
}

SPECIAL INSTRUCTIONS
--------------------
${order.delivery_instructions || 'None'}

ALLERGY INFORMATION
-------------------
${order.allergy_note || 'None'}

TOTAL: £${order.total_amount}
Payment: ${
        order.payment_method === 'cash_at_counter'
          ? 'Cash at Counter'
          : 'Card Payment'
      }
Status: ${order.status.toUpperCase()}

Generated: ${new Date().toLocaleString()}
      `.trim();

      const blob = new Blob([kotContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KOT_${
        order.kot_reference ||
        order.kotReference ||
        order.kot?.reference ||
        order.id
      }.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const handleShare = async () => {
      const kotText = `KOT: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }\nOrder #${order.id}\nTotal: £${order.total_amount}\nStatus: ${
        order.status
      }`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }`,
            text: kotText,
          });
        } catch (err) {
          console.log('Error sharing:', err);
        }
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(kotText).then(() => {
          showSuccessToast('KOT details copied to clipboard!', 'kot-share');
        });
      }
    };

    const handleQuickPrint = () => {
      if (on58mmPrint) {
        on58mmPrint(order);
      } else {
        window.print();
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Receipt className="h-8 w-8" />
                <div>
                  <h2 className="text-xl font-bold">Kitchen Order Ticket</h2>
                  <p className="text-green-100">
                    KOT Reference:{' '}
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleQuickPrint}
                  className="p-2 bg-green-500 hover:bg-green-400 rounded-lg transition-colors"
                  title="Quick Print"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="text-green-100 hover:text-white p-1 rounded"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* KOT Content */}
          <div className="p-6" id="kot-content">
            {/* Order Header */}
            <div className="border-b border-secondary-200 pb-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-bold text-lg text-secondary-900">
                    Order #{order.id}
                  </h3>
                  <p className="text-secondary-600">
                    <Clock className="inline h-4 w-4 mr-1" />
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-left md:text-right">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                    <span className="mr-1">📋</span>
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                  </div>
                  <p className="text-sm text-secondary-600 mt-1">
                    Customer ID: {order.user_id}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Type & Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Order Type
                </h4>
                <div className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg capitalize">
                    {order.order_type.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Payment Method
                </h4>
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg">
                    {order.payment_method === 'cash_at_counter'
                      ? 'Cash at Counter'
                      : 'Card Payment'}
                  </span>
                </div>
              </div>
            </div>

            {/* Vehicle Information - Add this new section for KOT */}
            {order.vehicle_data && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <Car className="h-5 w-5 mr-2" />
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Make
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.make}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Colour
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.colour}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Registration
                    </p>
                    <p className="font-bold text-blue-900 font-mono text-lg">
                      {order.vehicle_data.registrationNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="mb-6">
              <h4 className="font-bold text-lg text-secondary-900 mb-3 border-b border-secondary-200 pb-2">
                Order Items ({order.orderItems?.length || 0})
              </h4>
              <div className="space-y-3">
                {order.orderItems && order.orderItems.length > 0 ? (
                  order.orderItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-secondary-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="bg-green-600 text-white text-sm font-bold px-2 py-1 rounded-full min-w-[24px] text-center">
                              {item.quantity}
                            </span>
                            <h5 className="font-bold text-lg text-secondary-900">
                              {item.menuItem?.name ||
                                `Item #${item.menu_item_id}`}
                            </h5>
                          </div>
                          {item.menuItem?.description && (
                            <p className="text-sm text-secondary-600 ml-8 mb-2">
                              {item.menuItem.description}
                            </p>
                          )}

                          {/* Display addons in KOT view */}
                          {item.addons && item.addons.length > 0 && (
                            <div className="ml-8 bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                              <p className="text-sm font-bold text-blue-900 mb-2">
                                🍽️ Add-ons:
                              </p>
                              <div className="space-y-1">
                                {item.addons.map((addon, addonIndex) => (
                                  <div
                                    key={`${addon.id}-${addonIndex}`}
                                    className="flex justify-between items-center text-sm"
                                  >
                                    <div className="text-blue-800">
                                      <span className="font-bold">
                                        {addon.quantity}x
                                      </span>
                                      <span className="ml-2">
                                        {addon.menuItemAddon?.name ||
                                          `Addon #${addon.menu_item_addon_id}`}
                                      </span>
                                    </div>
                                    <div className="text-blue-900 font-medium">
                                      £
                                      {(
                                        addon.quantity *
                                        parseFloat(addon.price_at_time)
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.special_instructions && (
                            <div className="ml-8 bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                              <p className="text-sm text-yellow-800">
                                <strong>Special Instructions:</strong>{' '}
                                {item.special_instructions}
                              </p>
                            </div>
                          )}
                          {item.allergy_information && (
                            <div className="ml-8 bg-red-50 border border-red-200 rounded p-2 mt-2">
                              <p className="text-sm text-red-800">
                                <strong>⚠️ Allergy Info:</strong>{' '}
                                {item.allergy_information}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-left md:text-right ml-8 md:ml-4 mt-2 md:mt-0">
                          <p className="font-bold text-lg text-secondary-900">
                            £
                            {item.total_price
                              ? parseFloat(item.total_price).toFixed(2)
                              : (
                                  item.quantity * parseFloat(item.price)
                                ).toFixed(2)}
                          </p>
                          <p className="text-sm text-secondary-600">
                            £{item.price} base price
                          </p>
                          {item.addons && item.addons.length > 0 && (
                            <p className="text-xs text-green-600 font-medium">
                              + add-ons included
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-secondary-500">
                    No items found for this order
                  </div>
                )}
              </div>
            </div>

            {/* Special Instructions & Allergies */}
            {(order.delivery_instructions || order.allergy_note) && (
              <div className="space-y-3 mb-6">
                {order.delivery_instructions && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      📝 Special Instructions
                    </h4>
                    <p className="text-blue-800">
                      {order.delivery_instructions}
                    </p>
                  </div>
                )}
                {order.allergy_note && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2">
                      ⚠️ Allergy Information
                    </h4>
                    <p className="text-red-800">{order.allergy_note}</p>
                  </div>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-secondary-100 p-4 rounded-lg border border-secondary-200">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-secondary-900">
                  Order Total:
                </span>
                <span className="font-bold text-2xl text-secondary-900">
                  £{order.total_amount}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">
                  Payment Status:
                </span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    order.payment_status === 'paid'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {order.payment_status}
                </span>
              </div>
            </div>

            {/* Order Status */}
            <div className="text-center mt-4">
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full font-medium ${getStatusColor(
                  order.status,
                )}`}
              >
                Status: {order.status.toUpperCase().replace('_', ' ')}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-secondary-50 p-4 rounded-b-lg border-t border-secondary-200">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
              <div className="text-sm text-secondary-600 text-center md:text-left">
                Generated: {new Date().toLocaleString()}
              </div>
              <div className="flex flex-wrap justify-center md:justify-end gap-2">
                <button
                  onClick={handleShare}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center space-x-2 px-3 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          Failed to load orders
        </h3>
        <p className="text-secondary-600 mb-4">
          {error.response?.data?.message ||
            error.message ||
            'An error occurred while loading orders'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Handle case when orders is null or undefined
  if (!orders) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-secondary-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          No orders data
        </h3>
        <p className="text-secondary-600">Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Orders</h1>
        <p className="text-secondary-600">
          Manage and track incoming customer orders
        </p>
      </div>

      {/* Search and Filters - Tablet Optimized */}
      <div className="bg-white rounded-lg border border-secondary-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-secondary-900">
            Search & Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-700 underline self-start md:self-auto"
            >
              Clear all filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search orders, customers, items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full text-sm md:text-base"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Status</option>
            <option value="pending">⏳ Pending</option>
            <option value="pending_approval">🔍 Pending Approval</option>
            <option value="confirmed">✅ Confirmed</option>
            <option value="preparing">👨‍🍳 Preparing</option>
            <option value="ready">🎉 Ready</option>
            <option value="completed">✅ Completed</option>
            <option value="cancelled">❌ Cancelled</option>
          </select>

          <select
            value={orderTypeFilter}
            onChange={(e) => setOrderTypeFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Order Types</option>
            <option value="dine_in">🍽️ Dine-in</option>
            <option value="takeaway">📦 Takeaway</option>
            <option value="delivery">🚚 Delivery</option>
          </select>

          <select
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Payment Methods</option>
            <option value="stripe">💳 Card Payment</option>
            <option value="cash_at_counter">💵 Cash at Counter</option>
            <option value="apple_pay">🍎 Apple Pay</option>
            <option value="google_pay">🤖 Google Pay</option>
          </select>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="">All Dates</option>
              <option value="today">📅 Today</option>
              <option value="yesterday">📅 Yesterday</option>
              <option value="week">📅 This Week</option>
              <option value="month">📅 This Month</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="date">📅 Sort by Date</option>
              <option value="total">💰 Sort by Total</option>
              <option value="id">🆔 Sort by Order ID</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-sm md:text-base"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-secondary-600">
              📊 {filteredOrders.length} of {orders.length} orders
            </span>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() =>
            updateNotificationSettings({ sound: !notificationSettings.sound })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.sound
              ? 'bg-green-100 text-green-600 hover:bg-green-200'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          title={notificationSettings.sound ? 'Sound On' : 'Sound Off'}
        >
          {notificationSettings.sound ? (
            <Bell className="h-5 w-5" />
          ) : (
            <BellOff className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={() =>
            updateNotificationSettings({
              browser: !notificationSettings.browser,
            })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.browser
              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          title={
            notificationSettings.browser
              ? 'Browser Notifications On'
              : 'Browser Notifications Off'
          }
        >
          {notificationSettings.browser ? (
            <Smartphone className="h-5 w-5" />
          ) : (
            <Smartphone className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Orders List - Tablet Optimized */}
      <div className="space-y-3 md:space-y-4">
        {filteredOrders.map((order) => {
          const inlineActions = getInlineStatusActions(order);

          return (
            <div
              key={order.id}
              className="card hover:shadow-md transition-all duration-200 hover-lift"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="h-10 w-10 md:h-12 md:w-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <Package className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                      <h3 className="font-semibold text-secondary-900 text-sm md:text-base">
                        Order #{order.id}
                      </h3>
                      {(() => {
                        const kotReference =
                          order.kot_reference ||
                          order.kotReference ||
                          order.kot?.reference;
                        return (
                          kotReference && (
                            <span className="font-mono text-xs md:text-sm font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded flex items-center space-x-1">
                              <span>📋</span>
                              <span>KOT: {kotReference}</span>
                            </span>
                          )
                        );
                      })()}
                    </div>
                    <p className="text-xs md:text-sm text-secondary-600">
                      User #{order.user_id} • {order.orderItems?.length || 0}{' '}
                      items • {order.order_type}
                    </p>
                    <p className="text-xs text-secondary-500">
                      📅 {new Date(order.created_at).toLocaleString()}
                    </p>
                    {order.orderItems && order.orderItems.length > 0 && (
                      <p className="text-xs text-secondary-500 max-w-full md:max-w-xs truncate">
                        {order.orderItems
                          .slice(0, 2)
                          .map((item) => {
                            const itemName =
                              item.menuItem?.name ||
                              `Item #${item.menu_item_id}`;
                            const hasAddons =
                              item.addons && item.addons.length > 0;
                            return hasAddons
                              ? `${itemName} (+addons)`
                              : itemName;
                          })
                          .join(', ')}
                        {order.orderItems.length > 2 &&
                          ` +${order.orderItems.length - 2} more`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="text-left sm:text-right">
                    <p className="font-bold text-secondary-900 text-sm md:text-base">
                      💰 £{order.total_amount}
                    </p>
                    <span
                      className={`inline-flex items-center px-2 py-1 md:px-2.5 md:py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        order.status,
                      )}`}
                    >
                      {(() => {
                        const flow = getStatusFlow(order);
                        const currentStatus = flow.flow.find(
                          (s) => s.status === order.status,
                        );
                        return currentStatus
                          ? `${currentStatus.icon} ${currentStatus.label}`
                          : order.status;
                      })()}
                    </span>
                    <p className="text-xs text-secondary-500 mt-1">
                      {order.payment_method === 'cash_at_counter'
                        ? '💵 Cash'
                        : '💳 Card'}
                    </p>
                  </div>

                  {/* Enhanced Inline Actions Section */}
                  <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
                    {/* Status Actions */}
                    {inlineActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {inlineActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={action.action}
                            disabled={action.loading}
                            className={`
                              group relative flex items-center justify-center space-x-1.5 
                              px-3 py-2 text-white rounded-lg text-xs font-semibold 
                              ${action.color} disabled:opacity-50 
                              transition-all duration-200 hover:shadow-md hover:scale-105
                              active:scale-95 border border-transparent hover:border-white/20
                              min-w-[60px] sm:min-w-[80px]
                            `}
                            title={action.label}
                          >
                            {action.loading ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <span className="text-sm">{action.icon}</span>
                                <span className="hidden sm:inline font-medium">
                                  {action.label}
                                </span>
                              </>
                            )}
                            {/* Hover tooltip for mobile */}
                            <div
                              className="absolute -top-8 left-1/2 transform -translate-x-1/2 
                                          bg-gray-900 text-white text-xs px-2 py-1 rounded 
                                          opacity-0 group-hover:opacity-100 transition-opacity
                                          pointer-events-none whitespace-nowrap z-10 sm:hidden"
                            >
                              {action.label}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5 sm:gap-2 self-start sm:self-auto">
                    <button
                      onClick={() => handleOrderPrintBluetooth(order)}
                      disabled={bluetoothPrintingOrderId === order.id}
                      className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-all duration-200 hover:shadow-md hover:scale-105 active:scale-95 border border-transparent hover:border-white/20 min-w-[60px] sm:min-w-[80px] disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Print to Bluetooth thermal printer"
                    >
                      {bluetoothPrintingOrderId === order.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                      ) : (
                        <Printer className="h-3 w-3" />
                      )}
                      <span className="hidden sm:inline">
                        {bluetoothPrintingOrderId === order.id
                          ? 'Sending…'
                          : 'Print'}
                      </span>
                    </button>
                    {process.env.NODE_ENV === 'development' && (
                      <button
                        onClick={() => handleOrderPrint(order)}
                        className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-secondary-500 text-white rounded-lg text-xs font-semibold hover:bg-secondary-600 transition-all duration-200 hover:shadow-md hover:scale-105 active:scale-95 border border-transparent hover:border-white/20 min-w-[60px] sm:min-w-[80px]"
                        title="58mm print preview (dev only)"
                      >
                        <Receipt className="h-3 w-3" />
                        <span className="hidden sm:inline">58mm Preview</span>
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="p-2 text-secondary-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      title="View Order Details"
                    >
                      <Eye className="h-4 w-4 md:h-5 md:w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {searchTerm || statusFilter ? 'No orders found' : 'No orders yet'}
          </h3>
          <p className="text-secondary-600">
            {searchTerm || statusFilter
              ? 'Try adjusting your search terms or filters'
              : 'Orders will appear here when customers place them'}
          </p>
        </div>
      )}

      {/* Order Modal */}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          setKotOrder={setKotOrder}
          setShowKotView={setShowKotView}
          getStatusColor={getStatusColor}
          getStatusFlow={getStatusFlow}
          getStatusAction={getStatusAction}
          updateOrderStatusMutation={updateOrderStatusMutation}
        />
      )}

      {/* KOT Modal */}
      {showKotView && kotOrder && (
        <KotModal
          order={kotOrder}
          onClose={() => {
            setShowKotView(false);
            setKotOrder(null);
          }}
          on58mmPrint={handleOrderPrint}
        />
      )}
    </div>
  );
};

export default Orders;
